import * as React from 'react'
import { ChevronRight } from 'lucide-react'
import { NavLink } from 'react-router-dom'

import { cn } from '@/lib/utils'

interface SubNavigationProps {
  rootLabel: string
  children: {
    label: string
    to: string
    icon?: React.ReactElement
    /** Place at the far right of the bar (Settings), in line with the user menu above. */
    end?: boolean
  }[]
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-1.5 px-3 py-4 text-sm font-medium border-b-2 transition-colors',
    isActive
      ? 'border-gray-900 text-gray-900' // navy underline and text mark the active tab
      : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-900/30', // hover previews the active marker
  )

/**
 * Product-level tab bar. Shared by every workflow product (order confirmation,
 * sales order, admin) so a style change made for one shows up in all of them.
 */
export function SubNavigation({ rootLabel, children }: SubNavigationProps) {
  return (
    <div data-subnav className="sticky top-16 z-40 bg-white border-b border-canvas-border shadow-sm">
      <div className="mx-auto flex max-w-app items-center px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 pr-4 border-r border-gray-200 mr-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">
            {rootLabel}
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
        </div>

        <nav className="flex flex-1 items-center -mb-px" aria-label="Section navigation">
          {children.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={(state) => cn(linkClass(state), item.end && 'ml-auto')}
            >
              {item.icon
                ? React.cloneElement(item.icon, {
                    className: cn('h-4 w-4', item.icon.props.className),
                  })
                : null}
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  )
}
